/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, type ReactNode, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './LegalEvaluation.module.scss';

type Translate = (key: string) => string;

type Row = {
  n: string;
  title: string;
  body: ReactNode;
};

const getRows = (t: Translate): Row[] => [
  {
    n: '01',
    title: t('Bring entities you have already checked'),
    body: (
      <>
        {t(
          'Send a list you have run yourself, ideally one that gave you trouble. We return our record for each and show you every field where ours differs from yours, with the registry and filing date behind it.',
        )}{' '}
        <em>{t('Differences are the point of the exercise.')}</em>
      </>
    ),
  },
  {
    n: '02',
    title: t('Ask for coverage up front, not after'),
    body: (
      <>
        {t(
          'Depth varies by registry because it varies at source. We will tell you what is available in the jurisdictions you actually work in, field by field, including where a registry publishes little or nothing.',
        )}{' '}
        <em>{t('A gap stated up front is worth more than a gap found in month three.')}</em>
      </>
    ),
  },
  {
    n: '03',
    title: t('Trace one ownership chain end to end'),
    body: (
      <>
        {t(
          'Pick a group with holdings across several jurisdictions and follow the chain we return back to the filings it came from.',
        )}{' '}
        <em>
          {t('If any step cannot be traced to a document, that is worth knowing before the record goes near a file.')}
        </em>
      </>
    ),
  },
];

export const LegalEvaluation = () => {
  const { t } = useInt();
  const rowsRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(rowsRef, { threshold: 0.15 });

  return (
    <Section background="rgba(255, 255, 255, 0.55)" className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          align="left"
          title={t('Test it the way you would test a witness.')}
          description={t(
            'Company data is easy to demo and hard to trust. Rather than ask you to take coverage claims on faith, we would rather you tried to break them. Three ways to do that.',
          )}
        />
      </div>

      <div className={cn(styles.rows, revealed && styles.rowsOn)} ref={rowsRef}>
        {getRows(t).map((row, i) => (
          <div className={styles.row} style={{ '--d': `${i * 110}ms` } as CSSProperties} key={row.n}>
            <span className={styles.badge}>{row.n}</span>
            <div className={styles.rowBody}>
              <h3 className={styles.rowTitle}>{row.title}</h3>
              <p className={styles.rowText}>{row.body}</p>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.actions}>
        <Button type="blue" size="xl" href="/book-a-demo">
          {t('Book a demo')}
        </Button>
        <Button type="dark-outline" size="xl" href="/free-trial">
          {t('Run a group structure free')}
        </Button>
      </div>
    </Section>
  );
};
